import { useState, useCallback, useEffect, useRef } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase/config";
// Import cache utilities
import {
  getCachedData,
  setCachedData,
  invalidateCache,
  incrementReadCount,
  getReadCount,
} from "./utils/cacheUtils"; // Adjust path as needed

export const useStaticList = (type) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Removed hasFetchedOnce ref, cache handles this implicitly
  const isMounted = useRef(true); // Add mount check similar to the other hook

  const field = type === "brands" ? "brands" : "categories";
  const DOC_PATH =
    type === "brands"
      ? "staticContent/sortBrand"
      : "staticContent/sortCategory";

  // Generate cache key based only on document path for this hook
  const cacheKey = DOC_PATH;

  useEffect(() => {
    // Mount check effect
    return () => {
      isMounted.current = false;
    };
  }, []);

  const safeSetState = (setter, value) => {
    if (isMounted.current) {
      setter(value);
    }
  };

  const fetchList = useCallback(async () => {
    // --- Cache Check ---
    const cachedList = getCachedData(cacheKey);
    if (cachedList !== null) {
      safeSetState(setList, [...cachedList].reverse()); // Apply reverse logic to cached data too
      safeSetState(setLoading, false);
      safeSetState(setError, null);
      console.log(
        `[CACHE] Used cached data for ${type}. Total session reads: ${getReadCount()}`
      );
      return; // Don't fetch from Firestore
    }
    // --- End Cache Check ---

    const currentReadCount = incrementReadCount(); // Increment and get new count
    console.log(
      `🔥 [READ #${currentReadCount}] Fetching ${field} from ${DOC_PATH}`
    );
    safeSetState(setLoading, true);
    safeSetState(setError, null);

    try {
      const docRef = doc(db, DOC_PATH);
      const docSnap = await getDoc(docRef); // <<< ACTUAL FIRESTORE READ >>>

      if (docSnap.exists()) {
        const fetched = docSnap.data()?.[field] || [];
        const reversedList = [...fetched].reverse(); // Reverse before setting state/cache
        safeSetState(setList, reversedList);

        // --- Cache Update (store the non-reversed original array) ---
        setCachedData(cacheKey, fetched);
        // --- End Cache Update ---

        console.log(
          `✅ [READ #${currentReadCount}] Fetched ${
            fetched.length
          } ${field}. Total session reads: ${getReadCount()}`
        );
      } else {
        console.warn(
          `⚠️ [READ #${currentReadCount}] Document ${DOC_PATH} does not exist.`
        );
        safeSetState(setList, []);
        invalidateCache(cacheKey); // Invalidate if doc not found
      }
    } catch (err) {
      console.error(
        `❌ [READ #${currentReadCount}] Error fetching ${field}:`,
        err
      );
      safeSetState(setError, err.message);
      invalidateCache(cacheKey); // Invalidate on error
    } finally {
      safeSetState(setLoading, false);
    }
  }, [DOC_PATH, field, type, cacheKey]); // Added cacheKey

  const addItem = useCallback(
    async (newItem) => {
      // ... (validation logic remains same)
      const upperItem = newItem.trim().toUpperCase(); // Do validation before checking cache state

      // Check duplicate against current state (might be slightly stale if cache used, but avoids read)
      const isDuplicate = list.some(
        (existing) => existing.trim().toUpperCase() === upperItem
      );
      if (isDuplicate) {
        // Still throw based on local state check for immediate feedback
        throw new Error(`${type.slice(0, -1)} name already exists.`);
      }

      safeSetState(setLoading, true);
      safeSetState(setError, null);
      try {
        const docRef = doc(db, DOC_PATH);
        await updateDoc(docRef, {
          [field]: arrayUnion(upperItem),
        });

        // --- Cache Invalidation ---
        invalidateCache(cacheKey);
        // --- End Cache Invalidation ---

        // Optimistic Update + Refetch (like the other hook)
        // safeSetState(setList, (prev) => [upperItem, ...prev]); // Optimistic update
        // console.log(`✅ [UPDATE] Added ${type.slice(0, -1)}: ${upperItem}. Cache invalidated.`);
        // await fetchList(); // Force refetch

        // --- Refetch after invalidation ---
        console.log(
          `✅ [UPDATE] Added ${type.slice(
            0,
            -1
          )}: ${upperItem}. Invalidating cache and refetching...`
        );
        await fetchList(); // This will now fetch fresh data from Firestore
        // --- End Refetch ---
      } catch (err) {
        console.error(`❌ [UPDATE] Error adding ${field}:`, err);
        safeSetState(setError, err.message); // Use safeSetState
        // Don't re-throw if error is handled locally unless necessary upstream
        // throw err;
      } finally {
        safeSetState(setLoading, false); // Let fetchList handle loading state
      }
    },
    [list, DOC_PATH, field, type, cacheKey, fetchList] // Added cacheKey, fetchList
  );

  const deleteItem = useCallback(
    async (itemToDelete) => {
      // ... (validation logic remains same)

      safeSetState(setLoading, true);
      safeSetState(setError, null);
      try {
        const docRef = doc(db, DOC_PATH);
        await updateDoc(docRef, {
          [field]: arrayRemove(itemToDelete),
        });

        // --- Cache Invalidation ---
        invalidateCache(cacheKey);
        // --- End Cache Invalidation ---

        // Optimistic update + Refetch (like the other hook)
        // safeSetState(setList, (prev) => prev.filter((item) => item !== itemToDelete));
        // console.log(`✅ [UPDATE] Deleted ${field.slice(0, -1)}: ${itemToDelete}. Cache invalidated.`);
        // await fetchList(); // Force refetch

        // --- Refetch after invalidation ---
        console.log(
          `✅ [UPDATE] Deleted ${field.slice(
            0,
            -1
          )}: ${itemToDelete}. Invalidating cache and refetching...`
        );
        await fetchList(); // This will now fetch fresh data from Firestore
        // --- End Refetch ---
      } catch (err) {
        console.error(`❌ [UPDATE] Error deleting ${field}:`, err);
        safeSetState(setError, err.message); // Use safeSetState
      } finally {
        safeSetState(setLoading, false); // Let fetchList handle loading state
      }
    },
    [DOC_PATH, field, type, cacheKey, fetchList] // Added cacheKey, fetchList
  );

  // Initial fetch
  useEffect(() => {
    if (isMounted.current) {
      fetchList();
    }
  }, [fetchList]); // Dependency array unchanged

  // Return structure remains the same
  return {
    [type]: list,
    loading,
    error,
    [`add${type.slice(0, -1).charAt(0).toUpperCase()}${type.slice(1, -1)}`]:
      addItem,
    [`delete${type.slice(0, -1).charAt(0).toUpperCase()}${type.slice(1, -1)}`]:
      deleteItem,
    [`Workspace${type.charAt(0).toUpperCase() + type.slice(1)}`]: fetchList,
  };
};
